import axios from 'axios';
import tools from "@/composables/tools";

export interface Incidencia {
    id: number;
    title: string;
    text: string;
    estado: string;
    /** 0 = muy urgente; valores mayores = menos urgente (por defecto 50). */
    priority?: number | null;
    insert_userId?: number;
    created_at: string;
    updated_at: string;
    creador?: {
        id: number;
        nombre: string;
        mainImg?: string;
    };
    entidades?: Array<{
        id: number;
        nombre: string;
        mainImg?: string;
        pivot?: {
            reltype: string;
            insert_ts?: string;
            standBy_ts?: string;
        };
    }>;
    trackings?: Array<{
        id: number;
        text: string;
        created_at: string;
        updated_at: string;
        insert_uid: number;
        entidad?: {
            id: number;
            nombre: string;
            mainImg?: string;
        };
    }>;
    inventarios?: Array<{
        id: number;
        title: string | null;
        cantidad?: number;
    }>;
}

export interface IncidenciaFilter {
    search?: string;
    estado?: string;
    user_id?: number;
    relType?: string;
    page?: number;
    per_page?: number;
}

export interface IncidenciaGetOptions {
    /** Permite cancelar la petición cuando llega una búsqueda más reciente. */
    signal?: AbortSignal;
}

export interface PaginatedIncidencias {
    data: Incidencia[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number;
    to: number;
}

export abstract class IncidenciaService {

    static isCanceled(error: any): boolean {
        return axios.isCancel(error) || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';
    }

    /**
     * Listar incidencias
     */
    static async get(filter?: IncidenciaFilter, options?: IncidenciaGetOptions): Promise<PaginatedIncidencias | null> {
        try {
            console.log('IncidenciaService.get - Iniciando petición con filtros:', filter);
            const response = await axios.get('incidencias', {
                params: filter,
                timeout: 30000, // 30 segundos de timeout
                signal: options?.signal
            });
            console.log('IncidenciaService.get - Respuesta recibida:', response.data);
            return response.data;
        } catch (error: any) {
            // La cancelación la provoca el propio front al lanzar una búsqueda más
            // reciente: se propaga para que la llamada obsoleta se descarte sin ruido.
            if (IncidenciaService.isCanceled(error)) {
                throw error;
            }
            console.error('IncidenciaService.get - Error:', error);
            const errorMessage = error.response?.data?.message || error.message || 'No se pueden mostrar las incidencias';
            tools().setLog(errorMessage, 'error');
            tools().toast(errorMessage, 'danger', 5000);
            return null;
        }
    }

    /**
     * Obtener mis incidencias como responsable
     */
    static async getMisIncidenciasResponsable(): Promise<Incidencia[] | null> {
        // Polling en segundo plano (cada 60s): no debe mostrar toasts de error.
        try {
            const response = await axios.get('incidencias/responsable/list', { skipErrorToast: true });
            return response.data;
        } catch (error) {
            console.warn('No se pudieron obtener las incidencias asignadas (polling):', error);
            return null;
        }
    }

    /**
     * Obtener una incidencia por ID
     */
    static async getById(id: number): Promise<Incidencia | null> {
        try {
            const response = await axios.get(`incidencias/${id}`);
            return response.data;
        } catch (error) {
            tools().setLog('No se puede obtener la incidencia', 'error');
            tools().toast('No se puede obtener la incidencia', 'danger', 5000);
            return null;
        }
    }

    /**
     * Añadir tracking a una incidencia
     */
    static async addTracking(id: number, tracking: string): Promise<any | null> {
        try {
            const response = await axios.post(`incidencias/${id}/tracking`, {
                id,
                tracking,
            });
            tools().toast('Comentario añadido correctamente', 'success', 3000);
            return response.data;
        } catch (error: any) {
            console.error('Error añadiendo tracking:', error);
            const mensaje = error.response?.data?.message || 'Error añadiendo comentario';
            tools().setLog(mensaje, 'error');
            tools().toast(mensaje, 'danger', 5000);
            return null;
        }
    }

    /**
     * Bloquear/Desbloquear acceso del responsable
     */
    static async lockResponsable(id: number): Promise<Incidencia | null> {
        try {
            const response = await axios.post('incidencia/lockResponsable', { id });
            const action = response.data.lockResponsable ? 'bloqueado' : 'desbloqueado';
            tools().toast(`Acceso ${action} correctamente`, 'success', 3000);
            return response.data;
        } catch (error: any) {
            const mensaje = error.response?.data?.message || 'Error cambiando el estado de bloqueo';
            tools().setLog(mensaje, 'error');
            tools().toast(mensaje, 'danger', 5000);
            return null;
        }
    }

    /**
     * Actualizar estado de la incidencia
     */
    static async updateEstado(id: number, estado: string): Promise<boolean> {
        try {
            await axios.put(`incidencias/${id}/estado`, { estado });
            tools().toast(`Incidencia ${estado.toLowerCase()} correctamente`, 'success', 3000);
            return true;
        } catch (error: any) {
            const mensaje = error.response?.data?.message || 'Error actualizando el estado';
            tools().setLog(mensaje, 'error');
            tools().toast(mensaje, 'danger', 5000);
            return false;
        }
    }

    /**
     * Actualizar la prioridad de una incidencia (0 = muy urgente; default 50).
     */
    static async updatePrioridad(id: number, priority: number): Promise<number | null> {
        try {
            const response = await axios.put(`incidencias/${id}/prioridad`, { priority });
            tools().toast('Prioridad actualizada', 'success', 2000);
            return response.data.priority_nueva ?? priority;
        } catch (error: any) {
            const mensaje = error.response?.data?.message || 'Error actualizando la prioridad';
            tools().setLog(mensaje, 'error');
            tools().toast(mensaje, 'danger', 5000);
            return null;
        }
    }

    /**
     * Actualizar prioridades tras reordenar tarjetas.
     */
    static async updatePriorities(incidencias: { id: number; priority: number }[]): Promise<boolean> {
        if (!incidencias.length) return true;

        try {
            await axios.post('incidencias/priorities', { incidencias });
            return true;
        } catch (error: any) {
            const mensaje = error.response?.data?.message || 'Error actualizando prioridades';
            tools().setLog(mensaje, 'error');
            tools().toast(mensaje, 'danger', 5000);
            return false;
        }
    }

    /**
     * Asignar usuario a una incidencia (Responsable | Afectado).
     */
    static async attachUser(
        id: number,
        userId: number,
        reltype: 'Responsable' | 'Afectado'
    ): Promise<Incidencia | null> {
        try {
            const response = await axios.post(`incidencias/${id}/attach-user`, {
                user_id: userId,
                reltype,
            });
            tools().toast('Usuario asignado', 'success', 2500);
            return response.data.incidencia ?? null;
        } catch (error: any) {
            const mensaje = error.response?.data?.message || 'Error asignando usuario';
            tools().setLog(mensaje, 'error');
            tools().toast(mensaje, 'danger', 5000);
            return null;
        }
    }

    /**
     * Quitar usuario de una incidencia.
     */
    static async detachUser(
        id: number,
        userId: number,
        reltype?: 'Responsable' | 'Afectado'
    ): Promise<Incidencia | null> {
        try {
            const response = await axios.delete(`incidencias/${id}/detach-user`, {
                data: {
                    user_id: userId,
                    ...(reltype ? { reltype } : {}),
                },
            });
            tools().toast('Usuario desvinculado', 'success', 2500);
            return response.data.incidencia ?? null;
        } catch (error: any) {
            const mensaje = error.response?.data?.message || 'Error desvinculando usuario';
            tools().setLog(mensaje, 'error');
            tools().toast(mensaje, 'danger', 5000);
            return null;
        }
    }

    /**
     * Formatear fecha para mostrar
     */
    static formatDate(date: string): string {
        if (!date) return '-';
        const d = new Date(date);
        return d.toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    /**
     * Obtener color según estado
     */
    static getEstadoColor(estado: string): string {
        const colors: { [key: string]: string } = {
            'Abierta': 'warning',
            'En proceso': 'primary',
            'Resuelta': 'success',
            'Cerrada': 'medium',
        };
        return colors[estado] || 'medium';
    }

    static getPrioridadValue(priority?: number | null): number {
        return priority ?? 50;
    }

    static getPrioridadLabel(priority?: number | null): string {
        const value = this.getPrioridadValue(priority);
        if (value <= 9) return 'Muy urgente';
        if (value <= 29) return 'Alta';
        if (value <= 49) return 'Media';
        return 'Normal';
    }

    static getPrioridadColor(priority?: number | null): string {
        const value = this.getPrioridadValue(priority);
        if (value <= 9) return 'danger';
        if (value <= 29) return 'warning';
        if (value <= 49) return 'primary';
        return 'medium';
    }

    static formatPrioridad(priority?: number | null): string {
        const value = this.getPrioridadValue(priority);
        return `${this.getPrioridadLabel(priority)} (${value})`;
    }

    /**
     * Variables CSS del borde según prioridad (0 = más urgente).
     * El fondo de la tarjeta es fijo (rojo pálido); solo el borde refleja la urgencia.
     */
    static getIncidenciaUrgencyVars(priority?: number | null): Record<string, string> {
        const urgency = Math.max(0, Math.min(1, (50 - this.getPrioridadValue(priority)) / 50));
        const borderOpacity = 0.22 + urgency * 0.78;
        const borderWidth = 1 + urgency * 5;

        return {
            '--urgency-border': String(borderOpacity),
            '--urgency-border-width': `${borderWidth}px`,
        };
    }

    static isPrioridadAlta(priority?: number | null): boolean {
        return this.getPrioridadValue(priority) < 30;
    }

    /**
     * Poner una incidencia en stand-by (espera)
     */
    static async setStandBy(id: number, standBy_ts?: string): Promise<boolean> {
        try {
            const payload: any = { id };
            if (standBy_ts) {
                payload.standBy_ts = standBy_ts;
            }
            await axios.post('incidencias/standby', payload);
            tools().toast('Incidencia puesta en espera', 'success', 3000);
            return true;
        } catch (error: any) {
            const mensaje = error.response?.data?.message || 'Error al poner en espera';
            tools().setLog(mensaje, 'error');
            tools().toast(mensaje, 'danger', 5000);
            return false;
        }
    }

    /**
     * Quitar una incidencia del stand-by (reactivar)
     */
    static async removeStandBy(id: number): Promise<boolean> {
        try {
            await axios.delete('incidencias/standby', { data: { id } });
            tools().toast('Incidencia reactivada', 'success', 3000);
            return true;
        } catch (error: any) {
            const mensaje = error.response?.data?.message || 'Error al reactivar';
            tools().setLog(mensaje, 'error');
            tools().toast(mensaje, 'danger', 5000);
            return false;
        }
    }

    /**
     * Verificar si una incidencia está en stand-by
     */
    static isInStandBy(incidencia: Incidencia): boolean {
        if (!incidencia.entidades || incidencia.entidades.length === 0) return false;
        return incidencia.entidades.some(e => e.pivot?.standBy_ts != null);
    }
}
